import { useEffect, useState } from "react";
import { Sparkles, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Explicit choices only -- the backend never infers a timezone, so this list
// intentionally has no "auto-detect" option.
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "America/Puerto_Rico", "UTC",
];

/** "Call Selected with Sophia" -- Phase 1 section E. A separate action from
 * Delete Selected (which CrudModule already offers); operates ONLY on the
 * exact follow-up ids the human selected via CrudModule's existing checkbox
 * selection -- never the full filtered list, never the backlog. Every id is
 * revalidated server-side at both preflight and confirm time. */
export function SophiaSelectedCallDialog({ followupIds, open, onOpenChange, onLaunched }) {
  const { user } = useAuth();
  const [identities, setIdentities] = useState([]);
  const [identityId, setIdentityId] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [idemKey, setIdemKey] = useState("");
  const [result, setResult] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const canTestMode = ["admin", "ops_manager"].includes(user?.role);
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [schedTz, setSchedTz] = useState("America/New_York");
  const [allowWeekends, setAllowWeekends] = useState(false);
  const [schedBusy, setSchedBusy] = useState(false);
  const [draftSchedule, setDraftSchedule] = useState(null);

  const ids = followupIds || [];

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setPreflight(null);
    setIdemKey(newIdempotencyKey());
    setTestMode(false);
    setMode("now");
    setSchedDate("");
    setSchedTime("");
    setAllowWeekends(false);
    setDraftSchedule(null);
    api.get("/communication-identities", { params: { usable_only: true } })
      .then(({ data }) => setIdentities(data)).catch(() => setIdentities([]));
    loadPreflight("");
    setIdentityId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadPreflight = async (idValue) => {
    if (!ids.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/voice-ai/auto/followups/selected/preflight", {
        followup_ids: ids, communication_identity_id: idValue || null,
      });
      setPreflight(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the Sophia preflight checklist");
    } finally {
      setLoading(false);
    }
  };

  const onSelectIdentity = (val) => {
    setIdentityId(val);
    loadPreflight(val);
  };

  const confirm = async () => {
    if (!identityId || !preflight?.eligible_count) return;
    setLaunching(true);
    try {
      const { data } = await api.post("/voice-ai/auto/followups/selected/call-now", {
        followup_ids: ids, communication_identity_id: identityId,
        confirm: true, idempotency_key: idemKey, test_mode: testMode,
      });
      setResult(data);
      if (data.status === "completed") {
        toast.success(`${data.test_mode ? "TEST MODE: " : ""}Sophia is calling ${data.dialed_count} of ${data.requested_count} selected follow-up(s).`);
        onLaunched?.(data);
      } else {
        toast.error((data.blocking_reasons || [])[0] || "Sophia could not be launched for this selection.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not launch Sophia for this selection");
    } finally {
      setLaunching(false);
    }
  };

  const createDraftSchedule = async () => {
    if (!identityId || !schedDate || !schedTime || !schedTz) return;
    setSchedBusy(true);
    try {
      const { data } = await api.post("/voice-ai/auto/schedules", {
        followup_ids: ids, communication_identity_id: identityId,
        date: schedDate, time: schedTime, timezone: schedTz,
        allow_weekends: allowWeekends, test_mode: testMode,
      });
      setDraftSchedule(data);
      toast.success("Schedule saved as a draft — activate it separately to arm it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save this schedule");
    } finally {
      setSchedBusy(false);
    }
  };

  const activateDraftSchedule = async () => {
    if (!draftSchedule) return;
    setSchedBusy(true);
    try {
      await api.post(`/voice-ai/auto/schedules/${draftSchedule.id}/activate`, { confirm: true });
      setDraftSchedule({ ...draftSchedule, status: "scheduled" });
      toast.success(`Scheduled for ${schedDate} ${schedTime} ${schedTz}. Manage it from Agency Admin → Sophia Controls.`);
      onLaunched?.({ status: "scheduled", schedule_id: draftSchedule.id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not activate this schedule");
    } finally {
      setSchedBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="sophia-selected-dialog">
        <DialogHeader>
          <DialogTitle>Sophia Calls {ids.length} Selected Follow-Up{ids.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>
            Only the exact follow-ups you selected — never the full list, never the backlog.
            {preflight?.capped && " Selection was capped at the safe maximum."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-muted-foreground">Communication Identity</label>
            <Select value={identityId} onValueChange={onSelectIdentity}>
              <SelectTrigger data-testid="sophia-selected-identity"><SelectValue placeholder="Select a verified identity…" /></SelectTrigger>
              <SelectContent>
                {identities.map((id) => <SelectItem key={id.id} value={id.id}>{id.display_name} — {id.agency_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2" data-testid="sophia-selected-mode-toggle">
            <Button size="sm" variant={mode === "now" ? "default" : "outline"} onClick={() => setMode("now")}>
              Call Now
            </Button>
            <Button size="sm" variant={mode === "schedule" ? "default" : "outline"} onClick={() => setMode("schedule")} data-testid="sophia-selected-mode-schedule">
              <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Schedule for Later
            </Button>
          </div>

          {loading ? (
            <div className="text-xs text-muted-foreground">Checking eligibility…</div>
          ) : preflight ? (
            <div className="rounded-lg border p-2 text-xs" data-testid="sophia-selected-summary">
              <div className="mb-1 font-medium">
                {preflight.eligible_count} eligible · {preflight.blocked_count} blocked
              </div>
              <ul className="max-h-48 space-y-1 overflow-y-auto">
                {preflight.results.map((r) => (
                  <li key={r.followup_id} className={r.allowed ? "text-emerald-700" : "text-amber-700"}>
                    {r.client_name || r.followup_id}: {r.allowed ? "Ready" : (r.blocking_reasons || [])[0] || "Blocked"}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result && (
            <div className={`rounded border p-2 text-xs ${result.status === "completed" ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
                 data-testid="sophia-selected-result">
              {result.status === "completed"
                ? `${result.test_mode ? "TEST MODE: " : ""}Dialed ${result.dialed_count} of ${result.requested_count}.`
                : (result.blocking_reasons || [])[0] || `Not dialed (${result.status}).`}
            </div>
          )}

          {canTestMode && (
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="sophia-selected-test-mode-toggle">
              <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
              <span>TEST MODE — same eligibility checks, limited daily cap, recorded separately.</span>
            </label>
          )}
          {testMode && (
            <div className="rounded border border-amber-300 bg-amber-100 px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wider text-amber-900">
              Test Mode
            </div>
          )}

          {mode === "now" ? (
            <Button className="w-full" onClick={confirm}
                    disabled={!identityId || !preflight?.eligible_count || launching}
                    data-testid="sophia-selected-confirm">
              {launching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
              {testMode ? "Confirm — TEST MODE Sophia Calls" : `Confirm — Sophia Calls ${preflight?.eligible_count ?? 0} Eligible Client(s) Now`}
            </Button>
          ) : (
            <div className="space-y-2 rounded-lg border p-3" data-testid="sophia-selected-schedule-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Date</label>
                  <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="sophia-schedule-date" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Time</label>
                  <Input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} data-testid="sophia-schedule-time" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Timezone (explicit — never guessed)</label>
                <Select value={schedTz} onValueChange={setSchedTz}>
                  <SelectTrigger data-testid="sophia-schedule-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <input type="checkbox" checked={allowWeekends} onChange={(e) => setAllowWeekends(e.target.checked)} />
                <span>Allow Sunday (blocked by default)</span>
              </label>

              {!draftSchedule ? (
                <Button className="w-full" variant="outline" onClick={createDraftSchedule}
                        disabled={!identityId || !schedDate || !schedTime || schedBusy}
                        data-testid="sophia-schedule-save-draft">
                  {schedBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CalendarClock className="mr-1.5 h-4 w-4" />}
                  Save Schedule (Draft — Not Active Yet)
                </Button>
              ) : draftSchedule.status === "draft" ? (
                <div className="space-y-2">
                  <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="sophia-schedule-draft-banner">
                    Saved as a draft for {schedDate} {schedTime} {schedTz}. Not yet armed — activate to schedule the call.
                  </div>
                  <Button className="w-full" onClick={activateDraftSchedule} disabled={schedBusy} data-testid="sophia-schedule-activate">
                    {schedBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CalendarClock className="mr-1.5 h-4 w-4" />}
                    Confirm — Activate Schedule
                  </Button>
                </div>
              ) : (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900" data-testid="sophia-schedule-active-banner">
                  Scheduled and active for {schedDate} {schedTime} {schedTz}. Manage (pause/resume/cancel) from Agency Admin → Sophia Controls.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
